import bg from '@assets/images/dot-pattern-bg.png'
import { forwardRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Container } from '@/components/ui/container'
import { ImageXIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import type { Image } from '@/features/photoset/types.ts'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import type { ImageThumbnails } from '@/types.ts'

interface SpaceDetailModalProps {
	currentImage: Pick<Image, 'id' | 'name'>
	thumbnails?: ImageThumbnails
	onClose: () => void
	className?: string
	headerSlot?: ReactNode
	thumbnailsSlot?: ReactNode
	sidebarSlot?: ReactNode
}

export const GalleryModal = forwardRef<HTMLDivElement, SpaceDetailModalProps>(
	(
		{
			currentImage,
			thumbnails,
			headerSlot,
			thumbnailsSlot,
			sidebarSlot,
			className
		},
		ref
	) => {
		const { t } = useTranslation()

		return createPortal(
			<Container
				className={cn(
					'fixed inset-0 z-(--z-modal) grid grid-cols-[minmax(0,1fr)_auto_auto] grid-rows-[minmax(0,1fr)] bg-neutral-white',
					className
				)}
				fullHeight
				fullWidth
				ref={ref}
			>
				<div className='grid grid-cols-[minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto] gap-4 py-4 [&_button]:shrink-0 [&_h3]:font-medium [&_h3]:text-base [&_h3]:text-neutral-900 [&_h4]:mb-2 [&_h4]:text-base [&_h4]:text-neutral-900 [&_h5]:text-neutral-700 [&_h5]:text-sm'>
					<div
						className={cn(
							'grid grid-rows-[auto_minmax(0,1fr)] gap-4',
							sidebarSlot && 'pe-6'
						)}
					>
						{headerSlot}

						{thumbnails?.l ? (
							// biome-ignore lint/correctness/useImageSize: the image is from backend and the size is not known
							<img
								alt={currentImage.name}
								className='h-full w-full rounded-lg border border-black/20 bg-neutral-white object-contain'
								key={currentImage.id}
								src={thumbnails?.l}
							/>
						) : (
							<div
								className='flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg border border-black/20 bg-cover bg-neutral-white font-medium'
								style={{ backgroundImage: `url(${bg})` }}
							>
								<ImageXIcon size={32} />
								<p>
									{t('errors:unavailable', {
										subject: 'image'
									})}
								</p>
							</div>
						)}
					</div>

					{thumbnailsSlot}
				</div>

				{sidebarSlot && <Separator orientation='vertical' />}

				{sidebarSlot}
			</Container>,
			document.body
		)
	}
)

GalleryModal.displayName = 'GalleryModal'
